import { GET_INDEXING_JOB_DETAILS_NAME } from "@/lib/ai/tools/get-indexing-job-details/constants";
import type { GetIndexingJobDetailsOutput } from "@/lib/ai/tools/get-indexing-job-details/server";
import { GET_SEARCH_ANALYTICS_AGGREGATED_DATA_NAME } from "@/lib/ai/tools/get-search-analytics-aggregated-data/constants";
import type { GetSearchAnalyticsAggregatedDataOutput } from "@/lib/ai/tools/get-search-analytics-aggregated-data/server";
import { GET_SEARCH_ANALYTICS_RAW_DATA_NAME } from "@/lib/ai/tools/get-search-analytics-raw-data/constants";
import type { GetSearchAnalyticsRawDataOutput } from "@/lib/ai/tools/get-search-analytics-raw-data/server";
import { GET_SESSION_DETAIL_NAME } from "@/lib/ai/tools/get-session-detail/constants";
import type { GetSessionDetailOutput } from "@/lib/ai/tools/get-session-detail/server";
import { GET_USER_ANALYTICS_AGGREGATED_DATA_NAME } from "@/lib/ai/tools/get-user-analytics-aggregated-data/constants";
import type { GetUserAnalyticsAggregatedDataOutput } from "@/lib/ai/tools/get-user-analytics-aggregated-data/server";
import { GET_USER_ANALYTICS_KEY_EVENTS_NAME } from "@/lib/ai/tools/get-user-analytics-key-events/constants";
import type { GetUserAnalyticsKeyEventsOutput } from "@/lib/ai/tools/get-user-analytics-key-events/server";
import { GET_USER_ANALYTICS_RAW_DATA_NAME } from "@/lib/ai/tools/get-user-analytics-raw-data/constants";
import type { GetUserAnalyticsRawDataOutput } from "@/lib/ai/tools/get-user-analytics-raw-data/server";
import { LIST_BIGQUERY_NAME } from "@/lib/ai/tools/list-bigquery/constants";
import type { ListBigqueryOutput } from "@/lib/ai/tools/list-bigquery/server";
import { LIST_DASHBOARDS_NAME } from "@/lib/ai/tools/list-dashboards/constants";
import type { ListDashboardsOutput } from "@/lib/ai/tools/list-dashboards/server";
import { LIST_INDEX_WORKERS_NAME } from "@/lib/ai/tools/list-index-workers/constants";
import type { ListIndexWorkersOutput } from "@/lib/ai/tools/list-index-workers/server";
import { LIST_QUERY_GROUPS_NAME } from "@/lib/ai/tools/list-query-groups/constants";
import type { ListQueryGroupsOutput } from "@/lib/ai/tools/list-query-groups/server";
import { LIST_SEARCH_ANALYTICS_VIEWS_NAME } from "@/lib/ai/tools/list-search-analytics-views/constants";
import type { ListSaViewsOutput } from "@/lib/ai/tools/list-search-analytics-views/server";
import { LIST_SESSIONS_NAME } from "@/lib/ai/tools/list-sessions/constants";
import type { ListSessionsOutput } from "@/lib/ai/tools/list-sessions/server";
import { LIST_URL_GROUPS_NAME } from "@/lib/ai/tools/list-url-groups/constants";
import type { ListUrlGroupsOutput } from "@/lib/ai/tools/list-url-groups/server";
import { LIST_USER_ANALYTICS_VIEWS_NAME } from "@/lib/ai/tools/list-user-analytics-views/constants";
import type { ListUaViewsOutput } from "@/lib/ai/tools/list-user-analytics-views/server";
import type {
  VizDataset,
  VizField,
  VizMetricAggregation,
} from "@/lib/ai/visualization/data/schema";
import { decodeToonIfString } from "@/lib/ai/visualization/shared/transport";

/**
 * 可視化データソースとして扱う tool と output 型の対応表。
 *
 * @remarks
 * tool 側の output 契約変更を adapter コンパイルエラーとして検知するため、
 * `toolName -> output` の 1:1 をここで固定する。
 */
type VizSourceOutputByToolName = {
  [GET_SEARCH_ANALYTICS_AGGREGATED_DATA_NAME]: GetSearchAnalyticsAggregatedDataOutput;
  [GET_SEARCH_ANALYTICS_RAW_DATA_NAME]: GetSearchAnalyticsRawDataOutput;
  [GET_SESSION_DETAIL_NAME]: Exclude<GetSessionDetailOutput, null>;
  [GET_USER_ANALYTICS_AGGREGATED_DATA_NAME]: GetUserAnalyticsAggregatedDataOutput;
  [GET_USER_ANALYTICS_KEY_EVENTS_NAME]: GetUserAnalyticsKeyEventsOutput;
  [GET_USER_ANALYTICS_RAW_DATA_NAME]: GetUserAnalyticsRawDataOutput;
  [GET_INDEXING_JOB_DETAILS_NAME]: GetIndexingJobDetailsOutput;
  [LIST_BIGQUERY_NAME]: ListBigqueryOutput;
  [LIST_DASHBOARDS_NAME]: ListDashboardsOutput;
  [LIST_INDEX_WORKERS_NAME]: ListIndexWorkersOutput;
  [LIST_QUERY_GROUPS_NAME]: ListQueryGroupsOutput;
  [LIST_SEARCH_ANALYTICS_VIEWS_NAME]: ListSaViewsOutput;
  [LIST_SESSIONS_NAME]: ListSessionsOutput;
  [LIST_URL_GROUPS_NAME]: ListUrlGroupsOutput;
  [LIST_USER_ANALYTICS_VIEWS_NAME]: ListUaViewsOutput;
};

export type VizSourceToolName = keyof VizSourceOutputByToolName;

export type VizSourceSnapshot = {
  toolCallId: string;
  toolName: string;
  output: unknown;
};

/**
 * registry 登録用 adapter 契約。
 *
 * @remarks
 * registry から渡る `output` は unknown 起点だが、tool 名に応じた型で扱う契約。
 * runtime の fail-fast は各 adapter 内で必要最小限に実施する。
 */
export type VisualizationAdapter<TName extends VizSourceToolName> = {
  toolName: TName;
  adapt: (args: {
    output: VizSourceOutputByToolName[TName];
    source: VizDataset["source"];
  }) => VizDataset[];
};

/**
 * adapter 宣言の型推論を固定する薄い define ヘルパー。
 */
export function defineVisualizationAdapter<TName extends VizSourceToolName>(
  adapter: VisualizationAdapter<TName>,
): VisualizationAdapter<TName> {
  return adapter;
}

type AnyVisualizationAdapter = VisualizationAdapter<VizSourceToolName>;

export type VisualizationAdapterRegistry = {
  adapters: readonly AnyVisualizationAdapter[];
  sourceToolNames: ReadonlySet<VizSourceToolName>;
  resolve: (toolName: string) => AnyVisualizationAdapter | null;
  adaptSnapshot: (snapshot: VizSourceSnapshot) => VizDataset[];
};

/**
 * adapter 配列から lookup/adapt を提供する registry を構築する。
 *
 * @remarks
 * `unknown` -> tool 固有 output のキャスト境界をこの関数へ集約し、
 * registry 実装側の重複と unsafe cast の散在を防ぐ。
 */
export function createVisualizationAdapterRegistry(
  adapters: readonly AnyVisualizationAdapter[],
): VisualizationAdapterRegistry {
  const sourceToolNames = new Set<VizSourceToolName>(
    adapters.map((adapter) => adapter.toolName),
  );
  const adapterByToolName = new Map<VizSourceToolName, AnyVisualizationAdapter>(
    adapters.map((adapter) => [adapter.toolName, adapter]),
  );

  const resolve = (toolName: string): AnyVisualizationAdapter | null => {
    if (!sourceToolNames.has(toolName as VizSourceToolName)) return null;
    return adapterByToolName.get(toolName as VizSourceToolName) ?? null;
  };

  const adaptSnapshot = (snapshot: VizSourceSnapshot): VizDataset[] => {
    const adapter = resolve(snapshot.toolName);
    if (!adapter) return [];

    // history 復元経路では TOON 文字列が混在しうるため、adapter 呼び出し前に decode する。
    const normalizedOutput = decodeToonIfString(snapshot.output);
    const typedOutput =
      normalizedOutput as VizSourceOutputByToolName[typeof adapter.toolName];

    return adapter.adapt({
      output: typedOutput,
      source: {
        toolName: adapter.toolName,
        toolCallId: snapshot.toolCallId,
      },
    });
  };

  return {
    adapters,
    sourceToolNames,
    resolve,
    adaptSnapshot,
  };
}

function describeOutputType(value: unknown): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
}

/**
 * adapter 入力を object payload に限定する fail-fast guard。
 *
 * @remarks
 * 代表 adapter から順に silent empty fallback を減らすための共通境界。
 * TOON 文字列は decode 済みで扱い、非 object が来た時点で tool 出力契約崩れとして停止する。
 */
export function expectVisualizationObjectOutput<T extends object>(args: {
  toolName: string;
  output: unknown;
}): T & Record<string, unknown> {
  const normalized = decodeToonIfString(args.output);
  if (
    typeof normalized !== "object" ||
    normalized === null ||
    Array.isArray(normalized)
  ) {
    throw new Error(
      `[${args.toolName}] visualization adapter expected object output but received ${describeOutputType(normalized)}`,
    );
  }
  return normalized as T & Record<string, unknown>;
}

/**
 * optional array field の shape 崩れを adapter 入口で検知する。
 *
 * @remarks
 * 未設定は許容するが、存在して array でない値は downstream で握りつぶさず停止する。
 */
export function expectOptionalArrayField<T = unknown>(
  output: Record<string, unknown>,
  key: string,
  toolName: string,
): T[] | undefined {
  const value = output[key];
  if (value === null || value === undefined) {
    return undefined;
  }
  if (!Array.isArray(value)) {
    throw new Error(
      `[${toolName}] visualization adapter expected "${key}" to be an array`,
    );
  }
  return value as T[];
}

type DatasetFieldDef = {
  key: string;
  role: VizField["role"];
  type: VizField["type"];
  unit?: string;
  format?: VizField["format"];
  aggregation?: VizMetricAggregation;
  labelDomain?: VizField["labelDomain"];
};

type DatasetFieldKey<TFields extends readonly DatasetFieldDef[]> =
  TFields[number]["key"];

/**
 * adapter 内 dataset 宣言用の型安全ヘルパー。
 *
 * @remarks
 * `defaults` が `fields.key` 範囲外を参照した場合にコンパイルエラーにする。
 */
export function defineDatasetSpec<
  const TFields extends readonly DatasetFieldDef[],
>(args: {
  id: string;
  profile: NonNullable<VizDataset["profile"]>;
  fields: TFields;
  defaults?: {
    x?: DatasetFieldKey<TFields>;
    y?: readonly DatasetFieldKey<TFields>[];
    series?: DatasetFieldKey<TFields>;
    category?: DatasetFieldKey<TFields>;
    value?: DatasetFieldKey<TFields>;
  };
  notes?: string;
}) {
  return args;
}
